import { AssetType, AssetTypesEnum } from '@types';
import { compressImageToSize } from './imageUtils';
import { isPDF } from './fileValidation';

export type ProcessFileMetrics = {
  originalFileSize: number;
  finalFileSize: number;
  compressionTimeMs: number;
};

export const processFile = async (
  file: File,
  callback: (imageDataUrl: string, file: File, metrics?: ProcessFileMetrics) => void
) => {
  const originalFileSize = file.size;

  if (isPDF(file)) {
    // Handle PDF files directly without image processing
    const reader = new FileReader();
    reader.onload = () => {
      const pdfDataUrl = reader.result as string;
      // For PDFs, just pass through the file and data URL
      callback(pdfDataUrl, file, {
        originalFileSize,
        finalFileSize: originalFileSize,
        compressionTimeMs: 0
      });
    };
    reader.readAsDataURL(file);
    return;
  }

  const compressionStartTime = performance.now();
  try {
    const compressedFile = await compressImageToSize(file);
    const compressionTimeMs = Math.round(performance.now() - compressionStartTime);

    const reader = new FileReader();
    reader.onload = () => {
      callback(reader.result as string, compressedFile, {
        originalFileSize,
        finalFileSize: compressedFile.size,
        compressionTimeMs
      });
    };
    reader.readAsDataURL(compressedFile);
  } catch (error) {
    console.error('Error compressing image:', error);
    const compressionTimeMs = Math.round(performance.now() - compressionStartTime);
    // Fallback to original file if compression fails
    const reader = new FileReader();
    reader.onload = () => {
      callback(reader.result as string, file, {
        originalFileSize,
        finalFileSize: originalFileSize,
        compressionTimeMs
      });
    };
    reader.onerror = () => {
      console.error('Error reading file as fallback');
    };
    reader.readAsDataURL(file);
  }
};

export const validatePassword = (password: string) => {
  // Password rules:
  // 1. At least one lowercase letter
  const hasLowerCase = /(?=.*[a-z])/;

  // 2. At least one uppercase letter
  const hasUpperCase = /(?=.*[A-Z])/;

  // 3. At least one digit
  const hasDigit = /(?=.*\d)/;

  // 4. At least one special character (as defined by Cognito)
  const hasSpecialChar = /(?=.*[$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-])/;

  // 5. Minimum length of 8 characters and only allowed characters
  const validLength = /^[A-Za-z\d$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]{8,}$/;

  return (
    hasLowerCase.test(password) &&
    hasUpperCase.test(password) &&
    hasDigit.test(password) &&
    hasSpecialChar.test(password) &&
    validLength.test(password)
  );
};
interface AssetContent {
  title: string;
  lowerTitle: string;
  sides: {
    front: string;
    back: string;
    passenger: string;
    driver: string;
  };
}
export const getExtensionFromBase64 = (base64: string): string => {
  if (!base64 || typeof base64 !== 'string') return 'jpg';
  const match = base64.match(/^data:(.*?);base64,/);
  if (!match) return 'jpg';
  const mime = match[1];
  switch (mime) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'application/pdf':
      return 'pdf';
    default:
      return 'jpg';
  }
};
export const getAssetSpecificContent = (assetType: AssetType): AssetContent => {
  switch (assetType) {
    case AssetTypesEnum.CAR:
      return {
        title: 'Vehicle',
        lowerTitle: 'vehicle',
        sides: {
          front: 'Vehicle Front',
          back: 'Vehicle Back',
          passenger: 'Passenger side',
          driver: "Driver's side"
        }
      };
    case AssetTypesEnum.MOTOR_CYCLE:
      return {
        title: 'Motorbike',
        lowerTitle: 'motorbike',
        sides: {
          front: 'Motorbike Front',
          back: 'Motorbike Back',
          passenger: 'Left Side',
          driver: 'Right Side'
        }
      };
    case AssetTypesEnum.CAMPERVAN:
      return {
        title: 'Caravan',
        lowerTitle: 'caravan',
        sides: {
          front: 'Caravan Front',
          back: 'Caravan Back',
          passenger: 'Left Side',
          driver: 'Right Side'
        }
      };
    case AssetTypesEnum.TRAILER:
      return {
        title: 'Trailer',
        lowerTitle: 'trailer',
        sides: {
          front: 'Trailer Front',
          back: 'Trailer Back',
          passenger: 'Left Side',
          driver: 'Right Side'
        }
      };
    case AssetTypesEnum.EQUIPMENT:
      return {
        title: 'Heavy Vehicle / Equipment',
        lowerTitle: 'heavy vehicle / equipment',
        sides: {
          front: 'Heavy Vehicle / Equipment Front',
          back: 'Heavy Vehicle / Equipment Back',
          passenger: 'Left Side',
          driver: 'Right Side'
        }
      };
    case AssetTypesEnum.MARINE:
      return {
        title: 'Watercraft',
        lowerTitle: 'watercraft',
        sides: {
          front: 'Watercraft Front',
          back: 'Watercraft Back',
          passenger: 'Port',
          driver: 'Starboard'
        }
      };

    case AssetTypesEnum.MARINE_WITH_TRAILER:
      return {
        title: 'Watercraft with Trailer',
        lowerTitle: 'watercraft with trailer',
        sides: {
          front: 'Watercraft Front',
          back: 'Watercraft Back',
          passenger: 'Left side',
          driver: 'Right side'
        }
      };
    default:
      return {
        title: 'Asset',
        lowerTitle: 'asset',
        sides: {
          front: 'Asset Front',
          back: 'Asset Back',
          passenger: 'Left Side',
          driver: 'Right Side'
        }
      };
  }
};

// Export file validation utilities
export * from './fileValidation';

// Export geolocation manager
export { default as GeolocationManager } from './geolocationManager';

// Export formatters
export * from './formatters';

// Export user utilities
export * from './userUtils';

// Export image utilities
export * from './imageUtils';

// Export broker org utilities
export * from './brokerOrgUtils';

// Export session / preference localStorage helpers
export * from './sessionStorage';

